import React, { createContext, useContext, useMemo, useReducer } from 'react';
import type { FC } from 'react';
import type { AxiosInstance } from 'axios';
import type {
  AppUpdatesContextStateType,
  AppUpdatesActionsType,
  UpdatesStateType,
} from './types';
import {
  authReducer,
  deriveUserFields,
  updatesReducer,
  INITIAL_AUTH_STATE,
  INITIAL_UPDATES_STATE,
} from './reducers';

type PartialUpdatesState = Partial<UpdatesStateType> & {
  draftFilters?: Partial<UpdatesStateType['draftFilters']>;
  committedFilters?: Partial<UpdatesStateType['committedFilters']>;
  pagination?: Partial<UpdatesStateType['pagination']>;
};

interface AppUpdatesContextInitialState {
  user?: AppUpdatesContextStateType['user'];
  updates?: PartialUpdatesState;
}

interface AppUpdatesContextType {
  dispatch: React.Dispatch<AppUpdatesActionsType>;
  state: AppUpdatesContextStateType;
  api: AxiosInstance;
}

const INITIAL_STATE: AppUpdatesContextStateType = {
  ...INITIAL_AUTH_STATE,
  updates: INITIAL_UPDATES_STATE,
};

function reducer(
  state: AppUpdatesContextStateType,
  action: AppUpdatesActionsType,
): AppUpdatesContextStateType {
  switch (action.type) {
    case 'AUTHENTICATE': {
      const authState = authReducer(
        {
          user: state.user,
          isLoggedIn: state.isLoggedIn,
          loggedInUser: state.loggedInUser,
        },
        action,
      );
      return {
        ...state,
        ...authState,
      };
    }

    case 'UPDATES_CURSOR_SET':
    case 'UPDATES_LIMIT_SET':
    case 'UPDATES_DATA_LINKS_SET':
    case 'UPDATES_LOADING_SET':
    case 'UPDATES_ERROR_SET':
    case 'FILTER_APP_SELECT':
    case 'FILTER_STATUS_SELECT':
    case 'FILTER_CHANGE_TYPE_SELECT':
    case 'FILTER_SEARCH_SELECT':
    case 'UPDATES_FILTERS_APPLY':
    case 'UPDATES_FILTERS_RESET':
    case 'UPDATES_DRAFT_FILTERS_RESET':
      return {
        ...state,
        updates: updatesReducer(state.updates, action),
      };

    default:
      return state;
  }
}

const AppUpdatesContext = createContext<AppUpdatesContextType | null>(null);

export const useAppUpdatesContext = () => {
  const ctx = useContext(AppUpdatesContext);
  if (!ctx) {
    throw new Error(
      'useAppUpdatesContext must be used within <AppUpdatesContextProvider>',
    );
  }
  return ctx;
};

export interface AppUpdatesContextProviderProps {
  api: AxiosInstance;
  initialState?: AppUpdatesContextInitialState;
}

export const AppUpdatesContextProvider: FC<
  React.PropsWithChildren<AppUpdatesContextProviderProps>
> = ({ children, api, initialState }) => {
  const initialUser = initialState?.user ?? INITIAL_STATE.user;
  const initialStateMerged: AppUpdatesContextStateType = {
    user: initialUser,
    ...deriveUserFields(initialUser),
    updates: {
      ...INITIAL_STATE.updates,
      ...initialState?.updates,
      pagination: {
        ...INITIAL_STATE.updates.pagination,
        ...initialState?.updates?.pagination,
      },
      draftFilters: {
        ...INITIAL_STATE.updates.draftFilters,
        ...initialState?.updates?.draftFilters,
      },
      committedFilters: {
        ...INITIAL_STATE.updates.committedFilters,
        ...initialState?.updates?.committedFilters,
      },
    },
  };

  const [state, dispatch] = useReducer(reducer, initialStateMerged);

  const value = useMemo(
    () => ({ state, dispatch, api }),
    [state, dispatch, api],
  );

  return (
    <AppUpdatesContext.Provider value={value}>
      {children}
    </AppUpdatesContext.Provider>
  );
};
